import {
    AssignScopeCategory,
    AssignScopeElement,
    AssignScopesRequest,
    DeleteModelRequest,
    FeatureManagementType,
    Model,
    ModelScopeSettingsItemRespone,
    Permissions,
    SoftDeleteStatusType,
} from "@api/apiClient";

import { useAppSettings } from "@api/appSettings";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useAssignScopes, useDeleteModel, useModelScopes, useModelUsageInfo, useProjectModel } from "@api/models";
import MicroChip from "@components/MicroChip/MicroChip";
import useModelEdit from "@features/analysis/hooks/useModelEdit";
import { setSelectedNodesKeys, validateModelRow } from "@features/analysis/utils/treeGridUtils";
import useAppStore from "@hooks/useAppStore";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Grid, IconButton, ListItemText, ListSubheader, Snackbar, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import isGuid from "@utils/isGuid";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CreateVersionDialog from "./CreateVersionDialog";
import MuiModelGrid from "./MuiModelGrid";

const MuiModelTab = observer(() => {
  const { id: projectId } = useParams();
  const upStore = useUserPermissionStore();
  const [scopeId, setScopeId] = useState(0);
  const [openCreateVersionDialog, setOpenCreateVersionDialog] = useState(false);
  const modelGridApiRef = useGridApiRef();
  const appStore = useAppStore();
  const appSettings = useAppSettings();
  const confirm = useConfirm();
  const cadModelCategories = useCadModelCategories();
  const modelScopes = useModelScopes(appStore.model?.modelId, { enabled: false });
  const mutateAssignScopes = useAssignScopes(appStore.model?.modelId, { enabled: false });
  const projectModel = useProjectModel(appStore.model?.modelId, undefined, { enabled: false });
  const modelUsageInfo = useModelUsageInfo(appStore.model?.modelId);
  const [openToast, setOpenToast] = useState(false);
  const [modifiedRows, setModifiedRows] = useState<Set<string>>(new Set());

  const deleteModelMutation = useDeleteModel();

  // Use the new useModelEdit hook
  const {
    handleCreateVersionSubmit,
    handleUpdateVersion,
    isCreatingVersion,
    isUpdatingVersion,
    isLoading: isModelEditLoading,
  } = useModelEdit({
    modelGridApiRef,
    modifiedRows,
    setModifiedRows,
  });

  const getSelectedNodes = () => {
    setSelectedNodesKeys(modelGridApiRef, cadModelCategories.data, appStore);
  };

  const selectModel = async (model: Model | null | undefined) => {
    if (model) {
      appStore.setSelectedNodesKeys(null);
      await appStore.selectionManager.selectModel(model.apsViewId!);
      appStore.setModel(model);
      // Clear modified rows when switching models
      setModifiedRows(new Set());

      await Promise.all([
        appStore.queryClient.resetQueries({ queryKey: ["categories"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["elements"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["components"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["materials"], exact: false }),
      ]);

      appStore.setAssignNodes(null);

      await projectModel.refetch();
      await modelScopes.refetch();
    }
  };

  const handleModelChange = (event: any) => {
    const model = appStore.project?.models!.find((m: Model) => m.modelId === event.target.value);
    if (model && model !== appStore.model) {
      selectModel(model);
    }
  };

  const handleAutoAssignScopes = async () => {
    getSelectedNodes();

    const selectedModelCategories = { ...appStore.selectedModelCategories };

    const categoryScopes = Object.keys(selectedModelCategories).map((key: string) => {
      return new AssignScopeCategory({
        cadModelCategoryId: appStore.selectedModelCategories[key].data.cadModelCategoryId,
        scopeId,
      });
    });
    const elementScopes = Object.keys(appStore.selectedNodesKeys)
      .filter((key: string) => isGuid(key))
      .reduce((acc: any, key: string) => {
        if (!Object.hasOwnProperty.call(appStore.selectedModelCategories, key)) {
          acc.push(
            new AssignScopeElement({
              elementId: key,
              scopeId,
            }),
          );
        }
        return acc;
      }, []);

    const assignScopesRequest = new AssignScopesRequest({
      modelId: appStore.model?.modelId,
      categoryScopes,
      elementScopes,
    });

    await mutateAssignScopes.mutateAsync(assignScopesRequest as any);
    await clearSelectedNodes();
    await refreshAssignNodes();
    await modelScopes.refetch();
  };

  const refreshAssignNodes = async () => {
    if (projectModel.data) {
      await projectModel.refetch();
    }
    appStore.setAssignNodes(null);
  };

  const clearSelectedNodes = async () => {
    modelGridApiRef.current.setRowSelectionModel([]);
  };

  useEffect(() => {
    const go = async () => {
      await appStore.selectionManager.setMode("SingleModel");
      if (appStore.model) {
        selectModel(appStore.model);
        refreshAssignNodes();
      }
    };
    go();
  }, []);

  const scopes = useMemo(() => {
    return appSettings.data?.modelScopeSetting?.modelScopeSettingsItems?.reduce((acc: any, item: ModelScopeSettingsItemRespone) => {
      if (item.deleteStatus === SoftDeleteStatusType.Active) {
        acc[item.id!] = item;
      }
      return acc;
    }, {});
  }, [appSettings.data]);

  const handleScopeChange = (event: any) => {
    setScopeId(event.target.value);
  };

  const handleCreateNewVersion = async () => {
    setOpenCreateVersionDialog(true);
  };

  const handleUpdateVersionClick = async () => {
    const rows = modelGridApiRef.current.getSortedRowIds().map((id) => modelGridApiRef.current.getRow(id));
    if (hasValidationErrors(rows)) {
      setOpenToast(true);
      return;
    }

    const success = await handleUpdateVersion();
    if (!success) {
      setOpenToast(true);
    }
  };

  const hasValidationErrors = (rows: any[]) => {
    return rows.some((row) => {
      const errors = validateModelRow(row);
      if (Object.keys(errors).length > 0) {
        return true;
      }
      return false;
    });
  };

  const handleCreateVersionSubmitClick = async (suffix: string) => {
    if (!appStore.model) {
      console.error("No model available for version creation");
      return;
    }

    const rows = modelGridApiRef.current.getSortedRowIds().map((id) => modelGridApiRef.current.getRow(id));
    if (hasValidationErrors(rows)) {
      setOpenToast(true);
      return;
    }

    const success = await handleCreateVersionSubmit(suffix, appStore.model.modelId!);
    if (success) {
      setOpenCreateVersionDialog(false);
    } else {
      setOpenToast(true);
    }
  };

  const handleCloseCreateVersionDialog = () => {
    setOpenCreateVersionDialog(false);
  };

  const handleDeleteModel = async () => {
    if (!appStore.model || !isCreatedVersion(appStore.model) || !projectId) return;

    // Confirm deletion
    const modelName = appStore.model?.modelName;
    const { confirmed } = await confirm({
      title: i18n.t("scope-tab.delete-model-dialog.header", "Delete Model Version"),
      description: i18n.t("scope-tab.delete-model-dialog.message", `Are you sure you want to delete the model version "${modelName}"? This action cannot be undone.`),
      confirmationText: i18n.t("dialog.yes", "Yes"),
      cancellationText: i18n.t("dialog.cancel", "Cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (!confirmed) return;

    try {
      const request = new DeleteModelRequest({
        modelId: appStore.model.modelId!,
        projectId: projectId,
      });

      await deleteModelMutation.mutateAsync(request);
    } catch (error) {
      console.error("Error deleting model:", error);
    }
  };

  // Helper function to check if model is a created version (has suffix & version)
  const isCreatedVersion = (model: Model | null) => {
    return model && model.suffix && model.suffix.trim() && model.version;
  };

  return (
    <>
      <Box mt={1} sx={{ position: "relative", display: "flex", flexDirection: "column", minHeight: "100%", width: "100%" }}>
        <Stack direction={"row"} spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="model-label">{i18n.t("scope-tab.models-select.title")}</InputLabel>
            <Select labelId="model-label" id="model-id" value={appStore.model?.modelId || ""} label={i18n.t("scope-tab.models-select.title")} onChange={handleModelChange}>
              <ListSubheader>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>{i18n.t("scope-tab.models-select.title")}</Box>
                  <Typography color="text.secondary">{i18n.t("scope-tab.version.title")} </Typography>
                </Box>
              </ListSubheader>
              {appStore.project?.models
                ?.slice()
                ?.sort((a, b) => {
                  // Sort by creation date descending (latest first)
                  const dateA = new Date(a.createdAt || 0);
                  const dateB = new Date(b.createdAt || 0);
                  return dateB.getTime() - dateA.getTime();
                })
                ?.map((model: Model) => (
                  <MenuItem key={model.modelId} value={model.modelId}>
                    <ListItemText
                      sx={{
                        "& .MuiListItemText-primary": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        },
                      }}
                    >
                      <Box>{model?.modelName}</Box>
                      <MicroChip label={model?.modelVersion || ""} />
                    </ListItemText>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Stack>
        <Box mt={2} sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: "100px", width: "100%" }}>
          {appStore.model && (
            <>
              <MuiModelGrid modelId={appStore.model?.modelId} apiRef={modelGridApiRef} onRowModified={setModifiedRows} />
              <Grid my={1} container spacing={1} sx={{ alignItems: "center", justifyContent: "end" }}>
                {appStore.hasFeature(FeatureManagementType.ModelScopes) && (
                  <Grid>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                      <InputLabel id="scope-label">{i18n.t("scope-tab.scope-select.title")}</InputLabel>
                      <Select labelId="scope-label" id="scope-id" value={scopeId} label={i18n.t("scope-tab.scope-select.title")} onChange={handleScopeChange} autoWidth>
                        <ListSubheader>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box>{i18n.t("scope-tab.scope-select.title")}</Box>
                          </Box>
                        </ListSubheader>
                        {Object.keys(scopes).map((id: any) => (
                          <MenuItem key={id} value={id}>
                            <ListItemText
                              sx={{
                                "& .MuiListItemText-primary": {
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                },
                              }}
                            >
                              <Box>{scopes[id].name}</Box>
                            </ListItemText>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {appStore.hasFeature(FeatureManagementType.ModelScopes) && (
                  <Grid>
                    <Button
                      disabled={Object.keys(appStore.selectedNodesKeys || {}).length === 0 || !upStore.hasPermission(Permissions.App_Project_Model_Create)}
                      loading={!!mutateAssignScopes.isPending}
                      variant="contained"
                      onClick={handleAutoAssignScopes}
                    >
                      {i18n.t("scopes-tab.assign-button.title")}
                    </Button>
                  </Grid>
                )}
                <Grid>
                  <Button
                    disabled={
                      !isCreatedVersion(appStore.model) ||
                      !upStore.hasPermission(Permissions.App_Project_Model_Delete) ||
                      (modelUsageInfo.data ? !modelUsageInfo.data.isDeleteOrUpdateEligible : false)
                    }
                    loading={!!deleteModelMutation.isPending}
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteModel}
                    title={
                      modelUsageInfo.data && !modelUsageInfo.data.isDeleteOrUpdateEligible
                        ? `Cannot delete: ${modelUsageInfo.data.usageDetails?.join(", ") || "Unknown reason"}`
                        : undefined
                    }
                  >
                    {i18n.t("scope-tab.delete-model-button.title", "Delete Version")}
                  </Button>
                </Grid>
                {/* Conditional buttons based on whether model can be updated */}
                {isCreatedVersion(appStore.model) && modelUsageInfo.data?.isDeleteOrUpdateEligible === true ? (
                  <>
                    {/* Update button - direct action */}
                    <Grid>
                      <Button
                        disabled={!appStore.model || !upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
                        loading={isUpdatingVersion}
                        variant="contained"
                        color="secondary"
                        onClick={handleUpdateVersionClick}
                      >
                        {i18n.t("scope-tab.update-version-button.title", "Update")}
                      </Button>
                    </Grid>
                    {/* Create button - opens dialog */}
                    <Grid>
                      <Button
                        disabled={!appStore.model || !upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
                        variant="contained"
                        color="primary"
                        onClick={handleCreateNewVersion}
                      >
                        {i18n.t("scope-tab.create-new-version-button.title", "Create New")}
                      </Button>
                    </Grid>
                  </>
                ) : (
                  /* Single Create button when not updatable */
                  <Grid>
                    <Button
                      disabled={!appStore.model || !upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
                      variant="contained"
                      color="primary"
                      onClick={handleCreateNewVersion}
                    >
                      {i18n.t("scope-tab.create-new-version-button.title")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Box>

        {/* Create Version Dialog */}
        <CreateVersionDialog
          open={openCreateVersionDialog}
          onClose={handleCloseCreateVersionDialog}
          onCreateVersion={handleCreateVersionSubmitClick}
          currentModel={appStore.model}
          projectModels={appStore.project?.models || []}
        />
      </Box>
      <Snackbar
        open={openToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("scope-tab.multiplier.snackbar-message")}
        onClose={() => setOpenToast(false)}
        ContentProps={{
          sx: {
            backgroundColor: "#d0ebff",
            color: "#003366",
          },
        }}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setOpenToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
});

export default MuiModelTab;
